import { memo, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { IDDItem } from "../../components/common/DropdownMenu";
import { useAppSelector } from "../../store/hooks/hooks";
import Input from "../../components/common/Input";
import DropdownMenu from "../../components/common/DropdownMenu";

interface ToolInputRowProps {
	inputId: string;
	dropdownItems: IDDItem[];
	onInputChange: (inputName: string, value: number | null) => void;
	onSelectUnit: (inputId: string, toUnitId: string) => void;
	disabled?: boolean;
}

export const InputRow: React.FC<ToolInputRowProps> = memo(({
	inputId,
	dropdownItems,
	onInputChange,
	onSelectUnit,
	disabled = false
}) => {
	const input = useAppSelector(state => {
		const calc = state.pvtCalculator;
		if (!calc) return undefined;
		return (
			calc.inputs.find(item => item.id === inputId) ??
			calc.gInputs.find(item => item.id === inputId) ??
			calc.gasCompositionInputs.find(item => item.id === inputId)
		);
	});

	if (!input) {
		return null;
	}

	const { t } = useTranslation('pvt');
	const showHints = useAppSelector(state => state.settings.showHints);
	const tooltipContent = useMemo(() => {
		if (!showHints) {
			return undefined;
		}

		return (
			<Trans
				ns={'pvt'}
				i18nKey={`form_field_titles.${inputId}`}
				components={{
					sub: <sub />,
					sup: <sup />,
				}}
			/>
		);
	}, [showHints, inputId]);

	const labelContent = useMemo(() => {
		return (
			<Trans
				ns={"pvt"}
				i18nKey={`form_fields.${inputId}`}
				components={{
					sub: <sub />,
					sup: <sup />,
				}}
			/>
		);
	}, [inputId]);


	const onDDSelectItem = (selectedItemId: string) => {
		onSelectUnit(input.id, selectedItemId);
	}

	const value: number | null = useMemo(() => {
		if (input.currentValue == null)
			return null;

		if (input.currentUnitId != null && input.currentUnitId == input.userUnitId)
			return input.userValue;

		return parseFloat(input.currentValue.toFixed(4));
	}, [input]);

	return (
		<div className="pvt__input-group">
			<label className="pvt__input-label" htmlFor={input.id}>
				{labelContent}:
			</label>
			<div className={"pvt__input-field " + (input.unitCategoryId && input.currentUnitId ? "with-dd" : "")}>
				<Input
					value={value}
					onChange={onInputChange}
					name={input.queryName}
					id={input.id}
					tooltip={tooltipContent}
					disabled={disabled}
					error={input.error}
					errorParams={input.errorParams}
				/>
			</div>
			{input.unitCategoryId && input.currentUnitId && (
				<DropdownMenu
					inputId={input.id}
					items={dropdownItems}
					selectedItemId={input.currentUnitId}
					onSelect={onDDSelectItem}
					disabled={disabled}
				/>)
			}
		</div>
	);
});

InputRow.displayName = "PVTCalculatorInputRow";
